"use client";

import * as React from "react";
import { usePathname, useRouter } from "next/navigation";
// Import and process cities data once
import citiesData from "@/data/cities.json";
import { createCitySlug, normalizeCityName } from "@/utils/geo/cityHelpers";
import { cn } from "@/utils/cn";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { useQueryState } from "nuqs";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

interface City {
  id: number;
  statecode: string;
  latitude: string;
  longitude: string;
  city: string;
}

interface CitiesSelectProps {
  onCityChange: (city: string, lat: number, lon: number) => void;
  selectedCity: string | null;
}

// Process cities data once outside component
const processedCities = citiesData.map((city) => ({
  ...city,
  displayName: `${city.city}, ${city.statecode}`,
}));

// Create a lookup map for faster city finding
const cityLookup = new Map(
  processedCities.map((city) => [city.city.toLowerCase(), city]),
);

export const CitiesSelect = React.memo(function CitiesSelect({
  onCityChange,
  selectedCity,
}: CitiesSelectProps) {
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Use nuqs for URL state management
  const [cityQuery, setCityQuery] = useQueryState("city");

  // Use useMemo for selected city data
  const selectedCityData = React.useMemo(() => {
    if (!selectedCity) return null;
    return cityLookup.get(selectedCity.toLowerCase()) || null;
  }, [selectedCity]);

  const handleCitySelect = React.useCallback(
    async (cityWithState: string) => {
      const selectedCityData = processedCities.find(
        (city) => city.displayName === cityWithState,
      );

      if (selectedCityData) {
        const citySlug = normalizeCityName(selectedCityData.city);

        // Only update URL if we're on the home page
        if (pathname === "/") {
          await setCityQuery(citySlug);
        } else {
          // For other pages, use direct navigation
          router.push(`/?city=${encodeURIComponent(citySlug)}`);
        }

        onCityChange(
          selectedCityData.city,
          parseFloat(selectedCityData.latitude),
          parseFloat(selectedCityData.longitude),
        );
      }
      setOpen(false);
    },
    [setCityQuery, onCityChange, router, pathname],
  );

  const displayCity = selectedCityData
    ? selectedCityData.displayName
    : "Select city...";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between sm:w-[180px]"
        >
          {displayCity}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search city..." className="h-9" />
          <CommandList>
            <CommandEmpty>No city found.</CommandEmpty>
            <CommandGroup>
              <ScrollArea className="h-[200px]">
                {processedCities.map((city) => (
                  <CommandItem
                    key={city.id}
                    value={city.displayName}
                    onSelect={handleCitySelect}
                  >
                    {city.displayName}
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4",
                        selectedCityData?.id === city.id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
